(() => {
  const SELECTOR_ROOT = '[data-bundle-builder]';
  const SELECTOR_BTN = '[data-bundle-submit]';
  const SELECTOR_MSG = '[data-bundle-msg]';
  const SELECTOR_CB = '[data-bundle-checkbox]';
  const SELECTOR_MAIN_CB = '[data-role="main"]';

  const SELECTOR_BEFORE_TOTAL = '[data-before-total]';
  const SELECTOR_AFTER_TOTAL = '[data-after-total]';

  const SELECTOR_ITEM_BEFORE = '[data-item-before]';
  const SELECTOR_ITEM_AFTER = '[data-item-after]';

  const SELECTOR_PRODUCTS_JSON = 'script[data-bundle-products-json]';

  const SELECTOR_THUMBS_WRAP = '[data-bundle-thumbs]';
  const SELECTOR_THUMB_MAIN = '[data-thumb-role="main"]';
  const SELECTOR_THUMB_EXTRA = '[data-thumb-role="extra"][data-thumb-product-id]';
  const SELECTOR_BUNDLE_ITEM_MAIN = '[data-bundle-item="main"]';
  const SELECTOR_BUNDLE_ITEM_EXTRA = '[data-bundle-item="extra"][data-product-id]';
  const SELECTOR_THUMB_IMG = '[data-thumb-img]';

  function parseIntSafe(v, fallback = 0) {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function safeJsonParse(str) {
    try { return JSON.parse(str); } catch { return null; }
  }

  function setMessage(msgEl, text = '', isError = false) {
    if (!msgEl) return;
    msgEl.hidden = !text;
    msgEl.textContent = text || '';
    msgEl.style.color = isError ? '#c8102e' : '';
  }

  // Use Impulse money formatter if present
  function formatMoney(cents, moneyFormat) {
    try {
      if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
        return window.theme.Currency.formatMoney(cents, moneyFormat);
      }
    } catch (_) {}

    return (cents / 100).toFixed(2);
  }

  async function addItemsToCart(variantIds) {
    const payload = { items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })) };

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });

    const text = await res.text();
    const data = safeJsonParse(text);

    if (!res.ok) {
      const message =
        (data && (data.description || data.message)) ||
        text ||
        'Failed to add bundle';

      const err = new Error(message);
      err.status = res.status;
      err.payload = data;
      throw err;
    }

    return data || {};
  }

  function openCartDrawerBestEffort() {
    const candidates = [
      '.js-drawer-open-cart',
      '[data-drawer-open]',
      'a[href="/cart"]',
      'a[href^="/cart"]'
    ];

    for (const sel of candidates) {
      const el = document.querySelector(sel);
      if (el) {
        el.click();
        return true;
      }
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true }));
    return false;
  }

  function initBundleBuilder(root) {
    if (root.dataset.bundleInit === '1') return;
    root.dataset.bundleInit = '1';

    const btn = root.querySelector(SELECTOR_BTN);
    const msgEl = root.querySelector(SELECTOR_MSG);
    const checkboxes = Array.from(root.querySelectorAll(SELECTOR_CB));
    const mainCb = root.querySelector(SELECTOR_MAIN_CB);

    const beforeTotalEl = root.querySelector(SELECTOR_BEFORE_TOTAL);
    const afterTotalEl = root.querySelector(SELECTOR_AFTER_TOTAL);

    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const discountPercent = parseIntSafe(root.dataset.discountPercent, 0);
    const multiplier = 100 - discountPercent;

    const jsonEl = root.querySelector(SELECTOR_PRODUCTS_JSON);
    const bundleProducts = jsonEl ? safeJsonParse(jsonEl.textContent || '') : null;

    if (!btn || !checkboxes.length) return;

    // --- thumbs maps (productId -> element / checkbox)
    const thumbsWrap = root.querySelector(SELECTOR_THUMBS_WRAP);
    const thumbMainEl = thumbsWrap ? thumbsWrap.querySelector(SELECTOR_THUMB_MAIN) : null;

    const thumbExtraByProductId = new Map();
    if (thumbsWrap) {
      thumbsWrap.querySelectorAll(SELECTOR_THUMB_EXTRA).forEach((el) => {
        const pid = el.getAttribute('data-thumb-product-id');
        if (pid) thumbExtraByProductId.set(String(pid), el);
      });
    }

    const extraItemByProductId = new Map();
    root.querySelectorAll(SELECTOR_BUNDLE_ITEM_EXTRA).forEach((lineEl) => {
      const pid = lineEl.getAttribute('data-product-id');
      const cb = lineEl.querySelector(SELECTOR_CB);
      if (pid && cb) extraItemByProductId.set(String(pid), { lineEl, cb });
    });

    function getSelectedVariantIds() {
      return checkboxes
        .filter((cb) => cb.checked && !cb.disabled)
        .map((cb) => cb.value)
        .filter(Boolean);
    }

    function recalcTotals() {
      let before = 0;
      let after = 0;

      checkboxes.forEach((cb) => {
        if (!cb.checked || cb.disabled) return;
        before += parseIntSafe(cb.dataset.beforeCents, 0);
        after += parseIntSafe(cb.dataset.afterCents, 0);
      });

      if (beforeTotalEl) beforeTotalEl.textContent = formatMoney(before, moneyFormat);
      if (afterTotalEl) afterTotalEl.textContent = formatMoney(after, moneyFormat);
    }

    function setLinePrices(lineEl, beforeCents, afterCents) {
      if (!lineEl) return;
      const beforeEl = lineEl.querySelector(SELECTOR_ITEM_BEFORE);
      const afterEl = lineEl.querySelector(SELECTOR_ITEM_AFTER);
      if (beforeEl) beforeEl.textContent = formatMoney(beforeCents, moneyFormat);
      if (afterEl) afterEl.textContent = formatMoney(afterCents, moneyFormat);
    }

    function setThumbImg(el, imageUrl, altText) {
      if (!el) return;
      const img = el.querySelector(SELECTOR_THUMB_IMG);
      if (!img) return;
      if (!imageUrl) return;
      img.src = imageUrl;
      if (altText) img.alt = altText;
    }

    // ✅ NEW: show/hide thumbs based on checkbox state
    function syncThumbsVisibility() {
      // main thumb
      if (thumbMainEl && mainCb) {
        const showMain = mainCb.checked && !mainCb.disabled;
        thumbMainEl.style.display = showMain ? '' : 'none';
      }

      // extra thumbs
      extraItemByProductId.forEach(({ cb }, pid) => {
        const thumb = thumbExtraByProductId.get(pid);
        if (!thumb) return;
        const show = cb.checked && !cb.disabled;
        thumb.style.display = show ? '' : 'none';
      });
    }

    // Track user unchecking so we don’t auto-recheck on color change
    checkboxes.forEach((cb) => {
      cb.addEventListener('change', () => {
        if (!cb.checked) cb.setAttribute('data-user-unchecked', '1');
        else cb.removeAttribute('data-user-unchecked');
      });
    });

    // One handler for checkbox changes (totals + thumbs)
    function onCheckboxChange() {
      recalcTotals();
      syncThumbsVisibility();
    }
    checkboxes.forEach((cb) => cb.addEventListener('change', onCheckboxChange));

    function updateMainVariant(variant) {
      if (!variant || !variant.id || !mainCb) return;

      mainCb.value = String(variant.id);

      if (variant.available === false) {
        mainCb.checked = false;
        mainCb.disabled = true;
      } else {
        mainCb.disabled = false;
        if (!mainCb.checked) mainCb.checked = true;
      }

      const price = parseIntSafe(variant.price, 0);
      const compare = parseIntSafe(variant.compare_at_price, 0);
      const before = (compare && compare > price) ? compare : price;
      const after = Math.round((price * multiplier) / 100);

      mainCb.dataset.beforeCents = String(before);
      mainCb.dataset.afterCents = String(after);

      const mainLine = root.querySelector(SELECTOR_BUNDLE_ITEM_MAIN);
      setLinePrices(mainLine, before, after);

      const imgUrl =
        variant?.featured_media?.preview_image?.src ||
        variant?.featured_media?.preview_image ||
        variant?.featured_image?.src ||
        variant?.featured_image ||
        null;

      if (imgUrl) {
        setThumbImg(thumbMainEl, imgUrl, variant.name || '');
      }

      recalcTotals();
      syncThumbsVisibility();
    }

    function findVariantForColor(productObj, color) {
      if (!productObj || !Array.isArray(productObj.variants)) return null;
      return productObj.variants.find(v => String(v.option1).toLowerCase() === String(color).toLowerCase()) || null;
    }

    function updateExtrasToColor(color) {
      if (!bundleProducts || !Array.isArray(bundleProducts)) return;

      const extraLines = Array.from(root.querySelectorAll(SELECTOR_BUNDLE_ITEM_EXTRA));

      extraLines.forEach((lineEl) => {
        const pid = lineEl.getAttribute('data-product-id');
        const productObj = bundleProducts.find(p => String(p.id) === String(pid));
        if (!productObj) return;

        const matched =
          findVariantForColor(productObj, color) ||
          productObj.variants.find(v => v.available) ||
          productObj.variants[0];

        if (!matched) return;

        const cb = lineEl.querySelector(SELECTOR_CB);
        if (!cb) return;

        cb.value = String(matched.id);

        const price = parseIntSafe(matched.price, 0);
        const compare = parseIntSafe(matched.compare_at_price, 0);
        const before = (compare && compare > price) ? compare : price;
        const after = Math.round((price * multiplier) / 100);

        cb.dataset.beforeCents = String(before);
        cb.dataset.afterCents = String(after);

        if (matched.available === false) {
          cb.checked = false;
          cb.disabled = true;
        } else {
          cb.disabled = false;
          // user choice сохраняем; не ре-чекаем
        }

        setLinePrices(lineEl, before, after);

        // ✅ update extra thumb image BUT do not force show it if user unchecked
        const thumb = pid ? thumbExtraByProductId.get(String(pid)) : null;
        if (thumb && matched.image) {
          setThumbImg(thumb, matched.image, productObj.title || '');
        }
      });

      recalcTotals();
      syncThumbsVisibility();
    }

    // Listen to variantChange on product container (Impulse)
    const productRoot =
      root.closest('[data-section-type="product"]') ||
      document.querySelector('[data-section-type="product"]');

    if (productRoot) {
      if (window.jQuery) {
        window.jQuery(productRoot).on('variantChange', (evt, data) => {
          const variant = data?.variant || evt?.detail?.variant || null;
          if (!variant) return;

          updateMainVariant(variant);

          const color = variant.option1;
          if (color) updateExtrasToColor(color);
        });
      }

      productRoot.addEventListener('variantChange', (evt) => {
        const variant = evt?.detail?.variant;
        if (!variant) return;

        updateMainVariant(variant);

        const color = variant.option1;
        if (color) updateExtrasToColor(color);
      });
    }

    // Initial state
    recalcTotals();
    syncThumbsVisibility();

    btn.addEventListener('click', async () => {
      try {
        setMessage(msgEl, '', false);

        const ids = getSelectedVariantIds();
        if (!ids.length) {
          setMessage(msgEl, 'Select at least one item.', true);
          return;
        }

        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');

        await addItemsToCart(ids);

        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true }));

        openCartDrawerBestEffort();
        setMessage(msgEl, 'Bundle added to cart.', false);
      } catch (e) {
        let nice = 'Error adding bundle.';

        if (e?.status === 422) {
          nice = e.message || 'Some items are unavailable or sold out.';
        } else if (e?.message) {
          nice = e.message;
        }

        setMessage(msgEl, nice, true);
        console.error('[BundleBuilder] Error:', e);
      } finally {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
    });
  }

  function boot() {
    document.querySelectorAll(SELECTOR_ROOT).forEach(initBundleBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();